export interface CalculatorParams {
  profitSharing: number;
  monthlyHours: number;
  inssRate: number;
  fgtsRate: number;
  vacationRate: number;
  thirteenthSalaryRate: number;
  noticeRate: number;
  sickLeaveRate: number;
  paternityLeaveRate: number;
  terminationFineRate: number;
  mealAllowance: number;
  healthPlan: number;
  transportation: number;
  financialEducation: number;
  taxesRate: number;
  spreadRate: number;
  salary?: number;
  extras?: number;
  saleHourlyRate?: number;
  bySalary: boolean;
  bySaleHourlyRate: boolean;
  bySpread: boolean;
  clt: boolean;
}

export type CalculatorInput = Partial<
  Omit<CalculatorParams, "bySalary" | "bySaleHourlyRate" | "bySpread" | "clt">
> &
  Pick<CalculatorParams, "bySalary" | "bySaleHourlyRate" | "bySpread"> &
  Partial<Pick<CalculatorParams, "clt">>;

export interface CalculatorResult {
  salary: number;
  salaryPerHour: number;
  profitSharingCost: number;
  payrollBase: number;
  inssCost: number;
  fgtsCost: number;
  socialChargesCost: number;
  vacationCost: number;
  thirteenthSalaryCost: number;
  noticeCost: number;
  sickLeaveCost: number;
  paternityLeaveCost: number;
  laborObligationsCost: number;
  terminationCost: number;
  terminationFineCost: number;
  mealAllowanceCost: number;
  healthPlanCost: number;
  transportationCost: number;
  financialEducationCost: number;
  benefitsCost: number;
  monthlyCost: number;
  hourlyCost: number;
  saleValue: number;
  saleHourlyRate: number;
  taxes: number;
  taxesPerHour: number;
  spreadValue: number;
  spreadPercentage: number;
  scenarios: CalculatorResult[];
}

export interface CalculatorComputation {
  params: CalculatorParams;
  result: CalculatorResult;
}

export const DEFAULT_CALCULATOR_PARAMS: CalculatorParams = {
  profitSharing: 12,
  monthlyHours: 168,
  inssRate: 0.278,
  fgtsRate: 0.08,
  vacationRate: 0.1509,
  thirteenthSalaryRate: 0.1131,
  noticeRate: 0.0266,
  sickLeaveRate: 0.0142,
  paternityLeaveRate: 0.0002,
  terminationFineRate: 0.0102,
  mealAllowance: 1021,
  healthPlan: 850,
  transportation: 120,
  financialEducation: 100,
  taxesRate: 0.182,
  spreadRate: 0.17,
  salary: undefined,
  extras: undefined,
  saleHourlyRate: undefined,
  bySalary: false,
  bySaleHourlyRate: false,
  bySpread: false,
  clt: true,
};

export class CalculatorError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "CalculatorError";
  }
}

// Extras/Caju entram no custo como salário-equivalente após divisão por este fator.
export const EXTRAS_SALARY_DIVISOR = 1.5;

// Salário PJ é convertido para equivalente CLT dividindo por este fator.
export const PJ_SALARY_DIVISOR = 1.5;

export function toCltEquivalentSalary(amount: number, salaryType?: string | null): number {
  const isPj = (salaryType ?? "").trim().toUpperCase() === "PJ";
  return isPj ? amount / PJ_SALARY_DIVISOR : amount;
}

type NumericCalculatorField =
  | "profitSharing"
  | "monthlyHours"
  | "inssRate"
  | "fgtsRate"
  | "vacationRate"
  | "thirteenthSalaryRate"
  | "noticeRate"
  | "sickLeaveRate"
  | "paternityLeaveRate"
  | "terminationFineRate"
  | "mealAllowance"
  | "healthPlan"
  | "transportation"
  | "financialEducation"
  | "taxesRate"
  | "spreadRate";

const POSITIVE_FIELDS: NumericCalculatorField[] = [
  "profitSharing",
  "monthlyHours",
  "inssRate",
  "fgtsRate",
  "vacationRate",
  "thirteenthSalaryRate",
  "noticeRate",
  "sickLeaveRate",
  "paternityLeaveRate",
  "terminationFineRate",
  "mealAllowance",
  "healthPlan",
  "transportation",
  "financialEducation",
  "taxesRate",
  "spreadRate",
];

export function calculatePricing(input: CalculatorInput): CalculatorResult {
  const params = normalizeParams(input);
  return selectStrategy(params);
}

function normalizeParams(input: CalculatorInput): CalculatorParams {
  const merged: CalculatorParams = {
    ...DEFAULT_CALCULATOR_PARAMS,
    ...input,
  };

  for (const field of POSITIVE_FIELDS) {
    merged[field] = ensurePositiveNumber(merged[field], String(field));
  }

  if (merged.salary !== undefined) {
    merged.salary = ensurePositiveNumber(merged.salary, "salary");
  }

  if (merged.extras !== undefined) {
    merged.extras = ensureNonNegativeNumber(merged.extras, "extras");
  }

  // Extras/Caju compõem o salário usado no cálculo, divididos por 1.5 (CLT ou PJ).
  // Só se aplica quando o salário é informado; no driver valor-hora o salário
  // derivado já representa o equivalente total.
  if (merged.salary !== undefined && merged.extras) {
    merged.salary += merged.extras / EXTRAS_SALARY_DIVISOR;
  }

  if (merged.saleHourlyRate !== undefined) {
    merged.saleHourlyRate = ensurePositiveNumber(merged.saleHourlyRate, "saleHourlyRate");
  }

  merged.bySalary = Boolean(merged.bySalary);
  merged.bySaleHourlyRate = Boolean(merged.bySaleHourlyRate);
  merged.bySpread = Boolean(merged.bySpread);
  merged.clt = merged.clt ?? true;

  validateParams(merged);

  return merged;
}

function selectStrategy(params: CalculatorParams): CalculatorResult {
  const selectionCount = [params.bySalary, params.bySaleHourlyRate, params.bySpread].filter(Boolean)
    .length;

  if (selectionCount !== 2) {
    throw new CalculatorError("Exactly two drivers must be enabled.");
  }

  if (params.bySalary && params.bySpread) {
    return calculateFromSalaryAndSpread(params);
  }

  if (params.bySaleHourlyRate && params.bySpread) {
    return calculateFromHourlyRateAndSpread(params);
  }

  if (params.bySalary && params.bySaleHourlyRate) {
    return calculateFromSalaryAndHourlyRate(params);
  }

  throw new CalculatorError("Unsupported drivers combination.");
}

function calculateFromSalaryAndSpread(
  params: CalculatorParams,
  includeScenarios: boolean = true,
): CalculatorResult {
  const salary = requirePositiveValue(params.salary, "salary");
  const base = buildBaseCosts(params, salary);

  const saleValue = computeSaleValue(base.monthlyCost, params.taxesRate, params.spreadRate);
  const taxes = saleValue * params.taxesRate;
  const spreadValue = saleValue * params.spreadRate;
  const saleHourlyRate = saleValue / params.monthlyHours;
  const taxesPerHour = taxes / params.monthlyHours;
  const spreadPercentage = saleValue === 0 ? 0 : (spreadValue / saleValue) * 100;

  const result: CalculatorResult = {
    ...base,
    saleValue,
    saleHourlyRate,
    taxes,
    taxesPerHour,
    spreadValue,
    spreadPercentage,
    scenarios: [],
  };

  if (includeScenarios) {
    result.scenarios = buildSalaryScenarios(params, salary);
  }

  return result;
}

function calculateFromHourlyRateAndSpread(params: CalculatorParams): CalculatorResult {
  const hourlyRate = requirePositiveValue(params.saleHourlyRate, "saleHourlyRate");

  const saleValue = hourlyRate * params.monthlyHours;
  const taxes = saleValue * params.taxesRate;
  const spreadValue = saleValue * params.spreadRate;
  const monthlyCost = saleValue - (taxes + spreadValue);

  const salary = params.clt
    ? deriveCltSalary(monthlyCost, params)
    : deriveContractorSalary(monthlyCost, params);

  const roundedSalary = Math.round(salary * 100) / 100;

  return calculateFromSalaryAndSpread(
    {
      ...params,
      salary: roundedSalary,
    },
    true,
  );
}

function calculateFromSalaryAndHourlyRate(
  params: CalculatorParams,
  includeScenarios: boolean = true,
): CalculatorResult {
  const salary = requirePositiveValue(params.salary, "salary");
  const hourlyRate = requirePositiveValue(params.saleHourlyRate, "saleHourlyRate");
  const base = buildBaseCosts(params, salary);

  const saleValue = hourlyRate * params.monthlyHours;
  const taxes = saleValue * params.taxesRate;
  const spreadValue = saleValue - (taxes + base.monthlyCost);
  const taxesPerHour = taxes / params.monthlyHours;
  const spreadPercentage = saleValue === 0 ? 0 : (spreadValue / saleValue) * 100;

  const result: CalculatorResult = {
    ...base,
    saleValue,
    saleHourlyRate: hourlyRate,
    taxes,
    taxesPerHour,
    spreadValue,
    spreadPercentage,
    scenarios: [],
  };

  if (includeScenarios) {
    result.scenarios = buildSpreadScenarios(params, salary);
  }

  return result;
}

function buildBaseCosts(params: CalculatorParams, salary: number): CalculatorResult {
  const profitSharingCost = salary / params.profitSharing;
  const payrollBase = salary + profitSharingCost;
  const salaryPerHour = salary / params.monthlyHours;
  const inssCost = params.clt ? payrollBase * params.inssRate : 0;
  const fgtsCost = params.clt ? payrollBase * params.fgtsRate : 0;
  const socialChargesCost = payrollBase + inssCost + fgtsCost;

  const vacationCost = params.clt ? payrollBase * params.vacationRate : payrollBase / 24;
  const thirteenthSalaryCost = params.clt ? payrollBase * params.thirteenthSalaryRate : 0;
  const noticeCost = params.clt ? payrollBase * params.noticeRate : 0;
  const sickLeaveCost = params.clt ? payrollBase * params.sickLeaveRate : 0;
  const paternityLeaveCost = params.clt ? payrollBase * params.paternityLeaveRate : 0;

  const laborObligationsCost =
    socialChargesCost +
    vacationCost +
    thirteenthSalaryCost +
    noticeCost +
    sickLeaveCost +
    paternityLeaveCost;

  const terminationFineCost = params.clt ? payrollBase * params.terminationFineRate : 0;
  const terminationCost = laborObligationsCost + terminationFineCost;

  const mealAllowanceCost = params.clt ? params.mealAllowance : 0;
  const healthPlanCost = params.clt ? params.healthPlan : 0;
  const transportationCost = params.clt ? params.transportation : 0;
  const financialEducationCost = params.financialEducation;

  const benefitsCost =
    terminationCost +
    mealAllowanceCost +
    healthPlanCost +
    transportationCost +
    financialEducationCost;

  const monthlyCost = benefitsCost;
  const hourlyCost = monthlyCost / params.monthlyHours;

  return {
    salary,
    salaryPerHour,
    profitSharingCost,
    payrollBase,
    inssCost,
    fgtsCost,
    socialChargesCost,
    vacationCost,
    thirteenthSalaryCost,
    noticeCost,
    sickLeaveCost,
    paternityLeaveCost,
    laborObligationsCost,
    terminationCost,
    terminationFineCost,
    mealAllowanceCost,
    healthPlanCost,
    transportationCost,
    financialEducationCost,
    benefitsCost,
    monthlyCost,
    hourlyCost,
    saleValue: 0,
    saleHourlyRate: 0,
    taxes: 0,
    taxesPerHour: 0,
    spreadValue: 0,
    spreadPercentage: 0,
    scenarios: [],
  };
}

function buildSalaryScenarios(params: CalculatorParams, salary: number): CalculatorResult[] {
  const start = salary - 2000;

  return Array.from({ length: 7 }, (_value, index) => start + (index + 1) * 500)
    .filter((value) => value > 0)
    .map((value) =>
      calculateFromSalaryAndSpread(
        {
          ...params,
          salary: value,
        },
        false,
      ),
    );
}

function buildSpreadScenarios(params: CalculatorParams, salary: number): CalculatorResult[] {
  const baseHourlyRate = params.saleHourlyRate;

  if (baseHourlyRate === undefined) {
    return [];
  }

  const start = baseHourlyRate - 20;

  return Array.from({ length: 7 }, (_value, index) => start + (index + 1) * 5)
    .filter((value) => value > 0)
    .map((value) =>
      calculateFromSalaryAndHourlyRate(
        {
          ...params,
          salary,
          saleHourlyRate: value,
        },
        false,
      ),
    );
}

function computeSaleValue(monthlyCost: number, taxesRate: number, spreadRate: number): number {
  const denominator = taxesRate + spreadRate - 1;

  if (denominator === 0) {
    throw new CalculatorError("Invalid rates combination: denominator is zero.");
  }

  return -(monthlyCost / denominator);
}

function deriveCltSalary(monthlyCost: number, params: CalculatorParams): number {
  const totalRates =
    params.inssRate +
    params.fgtsRate +
    params.vacationRate +
    params.thirteenthSalaryRate +
    params.noticeRate +
    params.sickLeaveRate +
    params.paternityLeaveRate +
    params.terminationFineRate;

  const benefits =
    params.mealAllowance + params.transportation + params.healthPlan + params.financialEducation;

  const costWithoutBenefits = monthlyCost - benefits;
  const payrollBase = costWithoutBenefits / (1 + totalRates);

  return (payrollBase * 12) / 13;
}

function deriveContractorSalary(monthlyCost: number, params: CalculatorParams): number {
  const benefits = params.financialEducation;
  const costWithoutBenefits = monthlyCost - benefits;

  return (costWithoutBenefits * 12) / ((1 + 0.04514) * 12 + 1);
}

function validateParams(params: CalculatorParams): void {
  if (params.taxesRate + params.spreadRate >= 1) {
    throw new CalculatorError("Taxes plus spread must be less than 1.");
  }

  if (params.bySalary && params.salary === undefined) {
    throw new CalculatorError("Salary is required when the salary driver is selected.");
  }

  if (params.bySaleHourlyRate && params.saleHourlyRate === undefined) {
    throw new CalculatorError("Sale hourly rate is required when the hourly driver is selected.");
  }
}

function ensurePositiveNumber(value: unknown, field: string): number {
  const normalized = normalizeNumber(value, field);
  if (normalized <= 0) {
    throw new CalculatorError(`Field \"${field}\" must be greater than zero.`);
  }
  return normalized;
}

function ensureNonNegativeNumber(value: unknown, field: string): number {
  const normalized = normalizeNumber(value, field);
  if (normalized < 0) {
    throw new CalculatorError(`Field \"${field}\" must be zero or greater.`);
  }
  return normalized;
}

function normalizeNumber(value: unknown, field: string): number {
  if (typeof value === "number") {
    if (!Number.isFinite(value)) {
      throw new CalculatorError(`Field \"${field}\" must be a finite number.`);
    }
    return value;
  }

  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    if (Number.isNaN(parsed)) {
      throw new CalculatorError(`Field \"${field}\" must be a number.`);
    }
    return parsed;
  }

  throw new CalculatorError(`Field \"${field}\" must be provided.`);
}

function requirePositiveValue(
  value: number | undefined,
  field: keyof CalculatorParams | keyof CalculatorResult,
): number {
  if (value === undefined) {
    throw new CalculatorError(`Field \"${String(field)}\" is required.`);
  }

  if (value <= 0) {
    throw new CalculatorError(`Field \"${String(field)}\" must be greater than zero.`);
  }

  return value;
}
